import KML from 'ol/format/KML';
import Feature from 'ol/Feature';
import { Polygon, MultiPolygon } from 'ol/geom';
import { getArea } from 'ol/sphere';
import JSZip from 'jszip';

export const KML_MAX_BYTES = 50 * 1024 * 1024; // 50 MB
export const MIN_HA = 5;
export const MAX_HA = 100;
export const MAX_CANDIDATES = 5;

function hectares(geometry) {
  return getArea(geometry) / 10000;
}

function flattenToPolygons(features) {
  const candidates = [];

  features.forEach((feature) => {
    const geom = feature.getGeometry();
    if (!geom) return;

    const baseName = feature.get('name') || '';

    if (geom instanceof Polygon) {
      candidates.push({
        geometry: geom.clone(),
        label: baseName || `Polígono ${candidates.length + 1}`,
      });
    } else if (geom instanceof MultiPolygon) {
      geom.getPolygons().forEach((poly, index) => {
        candidates.push({
          geometry: poly.clone(),
          label: baseName
            ? `${baseName} (${index + 1})`
            : `Polígono ${candidates.length + 1}`,
        });
      });
    }
  });

  return candidates;
}

async function extractKmlText(file) {
  const name = file.name.toLowerCase();

  if (name.endsWith('.kmz')) {
    const zip = await JSZip.loadAsync(file);
    const kmlPath = Object.keys(zip.files).find(
      (path) => path.toLowerCase().endsWith('.kml') && !zip.files[path].dir,
    );
    if (!kmlPath) {
      throw new Error('NO_KML');
    }
    return zip.files[kmlPath].async('text');
  }

  if (name.endsWith('.kml')) {
    return file.text();
  }

  throw new Error('INVALID_EXT');
}

/**
 * @param {File} file
 * @returns {Promise<{ valid: Array, validTotal: number, invalidCount: number, totalFound: number }>}
 */
export async function parseKmlFile(file) {
  if (file.size > KML_MAX_BYTES) {
    throw new Error('FILE_TOO_LARGE');
  }

  const kmlText = await extractKmlText(file);
  const format = new KML();
  const features = format.readFeatures(kmlText, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  });

  if (!features.length) {
    throw new Error('NO_POLYGONS');
  }

  const rawCandidates = flattenToPolygons(features);

  if (!rawCandidates.length) {
    throw new Error('NO_POLYGONS');
  }

  const withArea = rawCandidates.map((candidate, index) => ({
    ...candidate,
    id: index,
    hectares: hectares(candidate.geometry),
  }));

  const valid = withArea.filter(
    (c) => c.hectares >= MIN_HA && c.hectares <= MAX_HA,
  );

  if (!valid.length) {
    throw new Error('NO_VALID_POLYGON');
  }

  return {
    valid: valid.slice(0, MAX_CANDIDATES),
    validTotal: valid.length,
    invalidCount: withArea.length - valid.length,
    totalFound: withArea.length,
  };
}

export function candidateToFeature(candidate) {
  return new Feature({ geometry: candidate.geometry.clone() });
}
